// The job server's CV page (/jobnet/cv/{id}) shown in the profile panel, restyled like the
// Talentspace design: the portal's font and colours, places and occupational groups as tags.
// Works only when the page is ours (production, same origin).

import { addFrameStyle } from '@/sections/frameBar';
import { tsText } from '@/components/Talentspace/texts';

const PIN_ICON = '<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">'
  + '<path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/></svg>';

// The groups come as one comma-joined text, but names have commas too
// ("… Krankenpflege, Rettungsdienst und Geburtshilfe", "Gesundheitsberufe, a.n.g."):
// - when the names carry their level ("– komplexe Tätigkeiten"), a group ends at its level
//   (a few groups have none, so a part starting with "Berufe " always opens a new one)
// - a part starting in lower case belongs to the one before
export const splitOccupationalGroups = (text: string): string[] => {
  const parts = text.split(/,\s+/).map((part) => part.trim()).filter(Boolean);
  const hasLevels = parts.some((part) => part.includes(' – '));
  const groups: string[] = [];
  let current = '';
  parts.forEach((part) => {
    const lowerCase = /^[a-zäöü]/.test(part);
    const levelPending = hasLevels && !current.includes(' – ') && !part.startsWith('Berufe ');
    const continues = current !== '' && (lowerCase || levelPending);
    current = continues ? `${current}, ${part}` : part;
    if (continues) groups[groups.length - 1] = current;
    else groups.push(current);
  });
  return groups;
};

const makeTag = (page: Document, text: string, withPin: boolean): HTMLElement => {
  const tag = page.createElement('span');
  tag.className = 'ts-cv-tag';
  tag.innerHTML = withPin ? PIN_ICON : '';
  tag.append(page.createTextNode(text));
  return tag;
};

// Text right inside the element (not in its heading child). A Google translation wraps it
// in <font> tags, those count as the element's own text too.
const isOwnText = (node: ChildNode) => node.nodeType === Node.TEXT_NODE || (node as Element).tagName === 'FONT';

const ownText = (element: Element): string => Array.from(element.childNodes)
  .filter(isOwnText)
  .map((node) => node.textContent || '')
  .join(' ')
  .replace(/\s+/g, ' ')
  .trim();

const replaceOwnText = (element: Element, tags: HTMLElement[]) => {
  Array.from(element.childNodes)
    .filter((node) => isOwnText(node) || (node as Element).tagName === 'BR')
    .forEach((node) => node.remove());
  const list = element.ownerDocument.createElement('div');
  list.className = 'ts-cv-tags';
  list.append(...tags);
  element.append(list);
};

const cvCss = (font: string, color: (name: string, fallback: string) => string) => `
  * { font-family: ${font} !important; color: ${color('--ts-text', '#495057')}; }
  /* The <font> wrappers of a Google translation take the look of the text they sit in */
  font { font-size: inherit !important; font-weight: inherit !important; line-height: inherit !important; color: inherit !important; }
  .container_prof, .container_prof p, .container_prof li, .container_prof div { font-size: 15px; line-height: 1.6; }
  .container_prof { margin-top: 32px !important; }
  h1 { font-size: 32px !important; line-height: 1.2 !important; font-weight: 700 !important; color: ${color('--ts-dark', '#111')}; margin-bottom: 12px; }
  h1 .age { font-size: inherit !important; line-height: inherit !important; font-weight: 400 !important; color: ${color('--ts-muted', '#6b6b6b')}; }
  h2 { font-size: 20px !important; font-weight: 700 !important; color: ${color('--ts-dark', '#111')}; }
  .line { display: none; }
  .location { font-size: 15px !important; margin: 0 0 12px !important; }
  .groups_head { font-size: 12px !important; font-weight: 600 !important; letter-spacing: .06em; color: ${color('--ts-muted', '#6b6b6b')}; margin-bottom: 8px; }
  .prof_group, .location_add { margin-top: 24px !important; }
  .big_line_2 { border-bottom-color: ${color('--ts-border', '#e5e5e5')} !important; margin: 32px 0 !important; }
  .ts-cv-tags { display: flex; flex-wrap: wrap; gap: 8px; }
  .ts-cv-tag { display: inline-flex; align-items: center; gap: 6px; padding: 4px 14px; border: 1px solid ${color('--ts-border', '#e5e5e5')};
    border-radius: 999px; background: ${color('--ts-bg-soft', '#f4f4f4')}; color: ${color('--ts-dark', '#111')}; font-size: 14px !important; line-height: 1.5 !important; }
  .ts-cv-tag svg { flex-shrink: 0; }
`;

// The job server's contact form ("Direkt kontaktieren", /jobnet/ang/contact_form.php): a fixed
// 600px table with the labels under the fields. It opens inside the CV page (or as its own page
// after sending). Until the backend sends new markup, its fields are moved here into the design's
// layout (ProfileContactForm): "Ihr Stellenangebot" and "Ihre Kontaktdaten", labels above.
const contactFormCss = (font: string, color: (name: string, fallback: string) => string) => `
  body { font-family: ${font} !important; }
  .ts-cf-page .main-content-block { padding: 48px; }
  .ts-cf-back { display: inline-flex; align-items: center; gap: 8px; margin-bottom: 24px; cursor: pointer;
    font-size: 16px; font-weight: 600; line-height: 1.6; color: ${color('--ts-dark', '#111')} !important; text-decoration: none !important; }
  .ts-cf-back svg { flex-shrink: 0; }
  .ts-cf-page h2 { font-size: 32px !important; line-height: 1.2 !important; font-weight: 700 !important; color: ${color('--ts-dark', '#111')}; margin: 0 0 8px !important; }
  form[name="contact_form"] { font-family: ${font} !important; font-size: 16px; line-height: 1.6; color: ${color('--ts-text', '#4a4a4a')}; }
  form[name="contact_form"] > p, form[name="contact_form"] > p b { margin: 0 0 24px; font-size: 16px; line-height: 1.6; }
  form[name="contact_form"] > p > br:last-child { display: none; }
  form[name="contact_form"] b { font-weight: 700; }
  .ts-cf-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
  .ts-cf-wide { grid-column: 1 / -1; }
  .ts-cf-label { display: block; margin-bottom: 8px; font-size: 14px; font-weight: 600; line-height: 1.6; color: ${color('--ts-text', '#4a4a4a')}; }
  .ts-cf-label span { color: inherit !important; }
  .ts-cf-field input[type="text"], .ts-cf-field .cc-input, .ts-cf-field select {
    display: block; width: 100% !important; box-sizing: border-box; min-height: 44px; margin: 0; padding: 9px 16px;
    border: 1px solid ${color('--ts-border-strong', '#cfcfcf')}; border-radius: 8px; background: #fff; box-shadow: none;
    font-family: ${font} !important; font-size: 16px; line-height: 24px; color: #212529; outline: none;
    transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out; }
  .ts-cf-field .cc-input.multi_lines { min-height: 134px; padding: 6px 12px; }
  .ts-cf-field input[type="text"]:focus, .ts-cf-field .cc-input:focus, .ts-cf-field select:focus {
    border-color: ${color('--ts-dark', '#111')}; box-shadow: 0 0 0 4px ${color('--ts-focus-ring', 'rgba(17, 17, 17, .15)')}; }
  .ts-cf-contact { margin-top: 48px; padding-top: 48px; border-top: 1px solid #dee2e6; }
  .ts-cf-contact h3 { margin: 0 0 24px; font-size: 18px; font-weight: 700; line-height: 1.2; text-transform: none; letter-spacing: normal;
    color: ${color('--ts-dark', '#111')}; }
  .ts-cf-note { margin: 16px 0 0; font-size: 14px; color: rgba(33, 37, 41, .75); }
  .ts-cf-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; margin-top: 48px; padding-top: 24px; border-top: 1px solid #dee2e6; }
  .ts-cf-actions input { min-height: 44px; margin: 0 !important; padding: 0 16px; border-radius: 8px; cursor: pointer;
    font-family: ${font} !important; font-size: 16px; line-height: 1.5; }
  .ts-cf-actions input[type="submit"] { font-weight: 700; color: #fff;
    border: 1px solid ${color('--portal-primary', '#ebb818')}; background: ${color('--portal-primary', '#ebb818')}; }
  .ts-cf-actions input[type="submit"]:hover { border-color: ${color('--portal-primary-hover', '#d4a410')}; background: ${color('--portal-primary-hover', '#d4a410')}; }
  .ts-cf-actions input[type="button"] { font-weight: 400; color: ${color('--ts-dark', '#111')};
    border: 1px solid ${color('--ts-dark', '#111')}; background: transparent; }
  .ts-cf-actions input:focus-visible { outline: none; box-shadow: 0 0 0 4px ${color('--ts-focus-ring', 'rgba(17, 17, 17, .15)')}; }
  @media (max-width: 575.98px) { .ts-cf-page .main-content-block { padding: 24px; } }
  @media (max-width: 767.98px) { .ts-cf-grid { grid-template-columns: 1fr; } }
`;

const CHEVRON_LEFT = '<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">'
  + '<path fill-rule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/></svg>';

// Field names of the form in the design's order; a field the list doesn't know stays in the offer part
const OFFER_FIELDS = ['job_choice', 'position', 'einsatzort', 'taetigkeit', 'gehaltsr', 'einstelldatum', 'bemerkung'];
const CONTACT_FIELDS = ['firma', 'ansprech', 'telefon', 'ba_kundennr', 'fadresse', 'plzort', 'email', 'homepage'];
const WIDE_FIELDS = ['job_choice', 'taetigkeit', 'bemerkung'];

// One table cell (field + its label text after the last <br>) as a design field: label above
const buildField = (page: Document, cell: Element): HTMLElement => {
  const name = cell.querySelector('[name]')?.getAttribute('name') || '';
  const field = page.createElement('div');
  field.className = WIDE_FIELDS.includes(name) ? 'ts-cf-field ts-cf-wide' : 'ts-cf-field';
  field.dataset.field = name;

  const label = page.createElement('label');
  label.className = 'ts-cf-label';
  label.id = `ts-cf-label-${name}`;
  const breaks = Array.from(cell.querySelectorAll('br'));
  const lastBreak = breaks[breaks.length - 1];
  while (lastBreak?.nextSibling) label.append(lastBreak.nextSibling);
  breaks.forEach((lineBreak) => lineBreak.remove());

  // Real inputs get a for= link; the contenteditable boxes are named by the label instead
  const control = cell.querySelector('.cc-input, input[type="text"], select') as HTMLElement | null;
  const isRequired = label.textContent?.includes('*') || false;
  if (control && control.matches('input, select')) {
    control.id = control.id || `ts-cf-${name}`;
    label.htmlFor = control.id;
  }
  if (control && control.matches('.cc-input')) {
    control.setAttribute('role', 'textbox');
    control.setAttribute('aria-labelledby', label.id);
    if (control.matches('.multi_lines')) control.setAttribute('aria-multiline', 'true');
    label.addEventListener('click', () => control.focus());
  }
  if (control && isRequired) control.setAttribute('aria-required', 'true');

  if (label.textContent?.trim()) field.append(label);
  field.append(...Array.from(cell.childNodes));
  // A cell with hidden inputs only (BA-Kundennummer on some sites) keeps its values, unseen
  if (!control) field.hidden = true;
  return field;
};

const buildGrid = (page: Document, fields: HTMLElement[]): HTMLElement => {
  const grid = page.createElement('div');
  grid.className = 'ts-cf-grid';
  grid.append(...fields);
  return grid;
};

const orderOf = (names: string[], field: HTMLElement) => {
  const index = names.indexOf(field.dataset.field || '');
  return index === -1 ? names.length : index;
};

const layoutContactForm = (page: Document, onBack?: () => void) => {
  const form = page.querySelector('form[name="contact_form"]') as HTMLFormElement | null;
  const table = form?.querySelector('table');
  if (!form || !table || form.dataset.tsLayout) return;
  form.dataset.tsLayout = '1';
  page.body.classList.add('ts-cf-page');

  // Abbrechen sits in the cell with the submit button
  const cells = Array.from(table.querySelectorAll('td'));
  const buttonCell = cells.find((cell) => cell.querySelector('input[type="submit"]'));
  const fields = cells.filter((cell) => cell !== buttonCell).map((cell) => buildField(page, cell));
  const contactFields = fields
    .filter((field) => CONTACT_FIELDS.includes(field.dataset.field || ''))
    .sort((a, b) => orderOf(CONTACT_FIELDS, a) - orderOf(CONTACT_FIELDS, b));
  const offerFields = fields
    .filter((field) => !contactFields.includes(field))
    .sort((a, b) => orderOf(OFFER_FIELDS, a) - orderOf(OFFER_FIELDS, b));

  const offer = page.createElement('section');
  offer.className = 'ts-cf-offer';
  offer.append(buildGrid(page, offerFields));

  const contact = page.createElement('section');
  contact.className = 'ts-cf-contact';
  const contactTitle = page.createElement('h3');
  contactTitle.textContent = tsText('contactDetailsTitle');
  const note = page.createElement('p');
  note.className = 'ts-cf-note';
  note.textContent = tsText('requiredFieldsNote');
  contact.append(contactTitle, buildGrid(page, contactFields), note);

  // Abbrechen first, then Absenden (the table floated them right in reverse)
  const actions = page.createElement('div');
  actions.className = 'ts-cf-actions';
  const buttons = Array.from(buttonCell?.querySelectorAll('input') || []);
  buttons.forEach((button) => button.removeAttribute('style'));
  actions.append(...buttons);

  // Its Abbrechen aims at the old portal's frame ("main_target"), which is not here
  const cancel = buttons.find((button) => button.type === 'button');
  if (cancel && onBack) cancel.onclick = () => onBack();

  form.append(offer, contact, actions);
  table.remove();

  // "Zurück zum Profil" above the title, as in the design
  const title = form.parentElement?.querySelector(':scope > h2');
  if (title && onBack) {
    const back = page.createElement('a');
    back.className = 'ts-cf-back';
    back.href = '#';
    back.innerHTML = CHEVRON_LEFT;
    back.append(page.createTextNode(tsText('backToProfile')));
    back.addEventListener('click', (event) => {
      event.preventDefault();
      onBack();
    });
    title.before(back);
  }
};

const portalLook = () => {
  const rootStyle = getComputedStyle(document.documentElement);
  return {
    font: getComputedStyle(document.body).fontFamily,
    color: (name: string, fallback: string) => rootStyle.getPropertyValue(name).trim() || fallback,
  };
};

// Added to every page of the frame, so the form is styled however it opens. Safe to call
// again: the style goes in once, the layout once per form. onBack shows the CV again.
export const restyleCvContactForm = (page: Document | null, onBack?: () => void) => {
  if (!page) return;
  if (!page.getElementById('ts-cv-contact-form')) {
    const { font, color } = portalLook();
    addFrameStyle(page, 'ts-cv-contact-form', contactFormCss(font, color));
  }
  layoutContactForm(page, onBack);
};

// Safe to call again: the style goes in once, the tags only while the page has none
// (a translation brings the plain text back)
export const restyleCvPage = (page: Document | null) => {
  if (!page?.querySelector('.container_prof')) return;

  // The portal's own font and design colours
  if (!page.getElementById('ts-cv-design')) {
    const { font, color } = portalLook();
    addFrameStyle(page, 'ts-cv-design', cvCss(font, color));
  }
  if (page.querySelector('.main_info .ts-cv-tags')) return;

  // Places as tags with a pin
  const location = page.querySelector('.main_info > .location');
  if (location && ownText(location)) {
    const places = ownText(location).split(/,\s*/).filter(Boolean);
    replaceOwnText(location, places.map((place) => makeTag(page, place, true)));
  }

  // Occupational groups as tags
  const groups = page.querySelector('.main_info .prof_group');
  if (groups && ownText(groups)) {
    replaceOwnText(groups, splitOccupationalGroups(ownText(groups)).map((group) => makeTag(page, group, false)));
  }
};
